import React from 'react';
import { useId } from 'react';

interface SavingsIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
}

/**
 * Savings icon component
 * Displays the custom savings SVG icon
 */
export const SavingsIcon: React.FC<SavingsIconProps> = ({ size, width, height, ...props }) => {
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;

  return (
    <svg
      width={size ?? width ?? 24}
      height={size ?? height ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      {...props}
    >
      <defs>
        <linearGradient id={id('linearGradient10')}>
          <stop offset="0" stopColor="#e382a0" stopOpacity="1" />
          <stop offset="1" stopColor="#d8677d" stopOpacity="1" />
        </linearGradient>
        <linearGradient id={id('linearGradient8')}>
          <stop offset="0" stopColor="#f2d4e8" stopOpacity="1" />
          <stop offset="1" stopColor="#da4c78" stopOpacity="1" />
        </linearGradient>
        <linearGradient id={id('linearGradient4')}>
          <stop offset="0" stopColor="#f2d4e8" stopOpacity="1" />
          <stop offset="1" stopColor="#e997b0" stopOpacity="1" />
        </linearGradient>
        <radialGradient
          id={id('radialGradient5')}
          xlinkHref={`#${id('linearGradient4')}`}
          cx="10.41081"
          cy="8.6248207"
          fx="10.41081"
          fy="8.6248207"
          r="11"
          gradientTransform="matrix(1.0238666,0,0,0.69098618,-0.01102868,3.2344364)"
          gradientUnits="userSpaceOnUse"
        />
        <radialGradient
          id={id('radialGradient5-2')}
          xlinkHref={`#${id('linearGradient8')}`}
          cx="12"
          cy="11.5"
          fx="12"
          fy="11.5"
          r="11"
          gradientTransform="matrix(1.0238666,0,0,0.88424842,-0.11420138,1.6321188)"
          gradientUnits="userSpaceOnUse"
        />
        <linearGradient
          id={id('linearGradient9')}
          xlinkHref={`#${id('linearGradient10')}`}
          x1="12.717077"
          y1="16.672157"
          x2="12.680167"
          y2="21.463175"
          gradientUnits="userSpaceOnUse"
        />
      </defs>

      <path
        d="M 3.6076867,12.248375 H 3.533696 c -0.5775918,0 -1.0238666,0.446275 -1.0238666,1.023866 v 1.18005 c 0,0.565466 -0.4584005,1.023866 -1.0238667,1.023866 -0.56546614,0 -1.02386659,-0.4584 -1.02386659,-1.023866 v -1.18005 c 0,-1.684266 1.38733309,-3.071599 3.07159989,-3.071599 z"
        fill="#d8677d"
        stroke="none"
      />
      <path
        d="m 18.314449,1.5932203 c -1.558385,0.4164882 -2.00121,1.1238034 -2.967478,2.4121604 l -4.095467,0.052061 C 7.653567,4.1031779 4.0338661,6.7366974 3.533696,10.200642 H 3.459705 v 2.047733 c 0,1.713578 1.4324323,3.268356 2.6724654,4.423423 l 0.3123661,2.743643 c 0.1265591,1.111621 0.9289309,2.047733 2.0477332,2.047733 h 1.006513 c 1.1188023,0 2.0477333,-0.92893 2.0477333,-2.047733 v -1.023866 h 2.793942 v 1.023866 c 0,1.118803 0.92893,2.047733 2.047733,2.047733 h 1.006513 c 1.118803,0 2.047733,-0.92893 2.047733,-2.047733 v -2.53167 c 0.663832,-0.378318 1.185478,-0.899964 1.563797,-1.563796 h 0.483937 c 1.118802,0 2.047733,-0.928931 2.047733,-2.047734 v -2.047733 c 0,-1.118802 -0.928931,-2.0477331 -2.047733,-2.0477331 H 21.248202 C 21.017136,7.8656007 20.421855,6.6895272 19.442437,5.7772177 L 19.182133,3.2938802 C 19.182076,2.7284383 18.87989,1.5932768 18.314449,1.5932203 Z"
        fill={`url(#${id('radialGradient5')})`}
        stroke="none"
      />
      <path
        d="m 6.132332,16.672158 0.3125,2.744141 c 0.126558,1.11162 0.930026,2.046875 2.048828,2.046875 h 1.005859 c 1.118801,0 2.048828,-0.928073 2.048828,-2.046875 V 18.453408 C 9.609523,18.246595 7.882119,17.56658 6.132332,16.672158 Z m 13.310546,0.212891 c -1.909361,0.859027 -3.58217,1.337581 -5.101562,1.53125 v 1 c 0,1.118802 0.928073,2.046875 2.046875,2.046875 h 1.007812 c 1.118802,0 2.046875,-0.928073 2.046875,-2.046875 z"
        fill={`url(#${id('linearGradient9')})`}
        stroke="none"
      />
      <path
        d="m 22.909986,9.7586987 c -0.574348,1.5889473 -0.677184,3.8087933 -0.193975,5.1433303 0.496143,-0.37603 0.821893,-0.968742 0.821893,-1.629788 v -2.047733 c 0,-0.570967 -0.24228,-1.091976 -0.627918,-1.4658093 z"
        fill="#df6c8f"
        stroke="none"
      />
      <g transform="matrix(1.0238666,0,0,1.0238666,0.44659844,-1.0618911)" fill="#64194a" stroke="none">
        <path d="m 16,10 h 0.01" fill="#64194a" stroke="none" />
        <path
          d="m 16.559322,9.568359 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 0.0098 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z"
          fill="#7d1f42"
          stroke="none"
        />
      </g>
      <path
        d="m 18.260756,15.90191 c 0.323555,0.272982 1.182122,0.631937 2.011042,0.365417 0.234444,-0.244638 0.447134,-0.507139 0.734436,-0.947352 -0.765334,0.27699 -1.075803,0.398122 -1.806531,0.168457 -0.730727,-0.229664 -2.251178,-0.988119 -2.251178,-0.988119 0,0 0.988675,1.128614 1.312231,1.401597 z"
        fill={`url(#${id('radialGradient5-2')})`}
        stroke="none"
      />
    </svg>
  );
};
